<template>
  <div class="row">
    <div class="col">
      <h2 v-if="saleResult === null" class="h4 text-center my-3">
        {{ isMobile ? $t('Your selected leads') : $t('Use the checkboxes to the left to add up to 5 leads') }}
      </h2>
      <div v-if="saleResult && !allFailed" class="row mb-3">
        <b-col class="col-12 pb-3">
          <div class="buy-lead d-flex align-items-center my-2 px-3 py-2 rounded h-100 fadein-details">
            <div class="float-md-left col-12 align-items-center d-flex buy-lead__bought">
              <div class="text-center text-md-left w-100">
                <h2 class="h4">
                  {{ $t('Thank you for your purchase.') }}
                </h2>
                <p class="h4 font-weight-light h5">
                  <i18n path="All successfully purchased leads have now been added to {myLeads}.">
                    <template v-slot:myLeads>
                      <router-link class="text-link text-reset" :to="{name:'my-leads'}">
                        {{ $t('My Leads') }}
                      </router-link>
                    </template>
                  </i18n>
                </p>
              </div>
            </div>
          </div>
        </b-col>
      </div>
      <div class="row">
        <div class="col">
          <b-table class="bg-white fadein-details" :items="leads" :fields="visibleFields" tbody-tr-class="itemised-leads" thead-class="d-none" striped>
            <template v-slot:cell(date)="data">
              <div class="first-col">
                <div>
                  {{ $d(parseJsonDate(data.item[0].request_date), 'short') }}<br>
                  {{ $d(parseJsonDate(data.item[0].request_date), 'time') }}
                </div>
              </div>
            </template>
            <template v-slot:cell(postcode)="data">
              {{ leadShortAddress(data.item.location, data.item[0].postcode) }}
            </template>
            <template v-slot:cell(workType)="data">
              <div :class="[{'text-right': (saleResult !== null)}]">
                <span>{{ data.item[0].work_type_description }}</span>
              </div>
            </template>
            <template v-slot:cell(remove)="data">
              <b-button v-if="saleResult === null" variant="link" class="p-0 m-0" @click="$emit('remove', data.item)">
                <font-awesome-icon size="sm" class="mr-2 float-left text-danger" :icon="'minus-circle'" />
                <span class="remove-lead">{{ $t('Remove') }}</span>
              </b-button>
            </template>
            <template v-slot:row-details="data">
              <div v-if="isPurchased(data.item)" class="d-flex align-items-center justify-content-between fadein-details">
                <span class="font-weight-normal">
                  <font-awesome-icon size="lg" class="mr-2 text-success" :icon="'check-circle'" /> {{ $t('Purchased') }}
                </span>
                <router-link :to="{name:'my-lead', params: {code:getLeadCode(data.item)}}" class="btn btn-outline-primary btn-sm btn-lead-view btn">
                  {{ $t('View Lead') }}
                </router-link>
              </div>
              <div class="text-danger font-weight-bold fadein-details" :show="isError(data.item)" v-html="getErrorMessage(data.item)" />
            </template>
          </b-table>
        </div>
      </div>
      <div v-if="saleResult === null">
        <div class="row">
          <div class="col text-center">
            <span class="text-secondary font-weight-bold">{{ $tc('{n}/5 leads Selected', leads.length) }}</span>
            <br>
            <span v-if="leads.length >= 5">{{ $t('(Max reached)') }}</span>
          </div>
        </div>

        <div v-if="overLimit > 0" class="row my-4 fadein-details">
          <div class="col-12">
            <b-alert class="mb-0" variant="danger" :show="true">
              <i18n
                path="overAccountLimit"
                :overLimit="$n(overLimit, 'currency')"
              >
                <template v-slot:settleAccount>
                  <router-link class="text-link text-link-reset" :to="{path:'/account/invoices', query: {settleAccount: true}}">
                    {{ $t('settle your account') }}
                  </router-link>
                </template>
                <template v-slot:contactCustomerSupport>
                  <router-link class="text-link text-link-reset" :to="{path:'/help/contact'}">
                    {{ $t('contact our customer support') }}
                  </router-link>
                </template>
              </i18n>
            </b-alert>
          </div>
        </div>

        <div v-if="!busy" class="row fadein-details">
          <b-col class="col-12 mb-2">
            <div class="buy-lead p-3 mt-2 rounded">
              <b-row>
                <b-col>
                  <p class="mb-3 small">
                    <i18n path="buyLeadsConfirm">
                      <template v-slot:price>
                        {{ $n(lead.dashboardPrice, 'currency') }}
                      </template>
                      <template v-slot:plusVat>
                        <small class="vat-text text-reset">{{ $t('+ VAT') }}</small>
                      </template>
                    </i18n>
                  </p>
                </b-col>
              </b-row>

              <b-row>
                <b-col class="col-12 d-flex justify-content-center lead-actions">
                  <b-button class="mr-1" size="sm" variant="outline-secondary" @click="$emit('cancel')">
                    {{ $t('Cancel') }}
                  </b-button>
                  <b-button
                    class="ml-1"
                    size="sm"
                    variant="primary"
                    :disabled="buyDisabled"
                    @click="(confirm=true) && buy()"
                  >
                    {{ $tc('Buy Lead', leads.length)}
                  </b-button>
                </b-col>
              </b-row>
            </div>
          </b-col>
        </div>
        <div v-else class="row my-3">
          <div class="col">
            <spinner :small="true" />
          </div>
        </div>
      </div>
      <div v-else class="row fadein-details">
        <b-col v-if="!(saleResult && !allFailed)" class="col-12 pb-3">
          <div class="buy-lead d-flex align-items-center my-2 px-3 py-2 rounded h-100">
            <div class="float-md-left col-12 align-items-center d-flex buy-lead__bought">
              <div class="text-center w-100">
                <b-button variant="primary" size="sm" @click="clearBasket">
                  {{ $t('CLEAR') }}
                </b-button>
              </div>
            </div>
          </div>
        </b-col>
      </div>
    </div>
  </div>
</template>

<script>
import Spinner from "@/components/animation/Spinner";
import {ACTIONED_LEADS, CLEAR_SELECTED_LEADS, SELECTED_LEADS} from "@/store/leads/mutation-types";
import LeadShortAddress from "@/imports/mixins/leadShortAddress";
import userDevice from "@/imports/mixins/userDevice";
import gaEvent from "@/imports/mixins/gaEvent";
import gaTransaction from "@/imports/mixins/gaTransaction";
import dates from '@/services/date';

export default {
  name: "BulkBuy",
  components: {Spinner},
  mixins: [LeadShortAddress, userDevice, gaEvent, gaTransaction],
  data: function () {
    return {
      creditLimit: null,
      confirm: false,
      busy: false,
      saleResult: null,
    }
  },
  computed: {
    fields: function() {
      return [
        {key:'date', label: this.$t('Date & Time'), class: 'align-middle'},
        {key:'postcode', label: this.$t('Postcode'), class: 'align-middle'},
        {key:'workType', label: this.$t('Lead Type'), class: 'align-middle'},
        {key:'remove', label:'', class: 'align-middle'},
      ];
    },
    totalCost: function() {
      return (this.leads.length > 0)
        ? this.leads.map(lead => parseFloat(lead.dashboardPrice)).reduce((prev, next) => prev + next)
        : 0;
    },
    buyDisabled: function() {
      return this.leads.length < 1 || this.overLimit > 0;
    },
    leads: {
      get: function() {
        return this.$store.state.leads.SELECTED_LEADS;
      },
      set: function(value) {
        this.$store.commit(SELECTED_LEADS, value);
      }
    },
    overLimit: function() {
      if (this.creditLimit === null) {
        return 0;
      }
      return this.totalCost - this.creditLimit;
    },
    allFailed: function() {
      if (this.saleResult) {
        return (Object.keys(this.saleResult.error).length > 0 && Object.keys(this.saleResult.leads).length === 0)
      }
      return true;
    },
    actionedLeads: {
      get: function() {
        return this.$store.state.leads.ACTIONED_LEADS;
      },
      set: function(value) {
        this.$store.commit(ACTIONED_LEADS, value);
      }
    },
    visibleFields: function () {
      return this.fields.filter((field) => (this.saleResult === null || field.key !== 'remove'));
    },
    company: function() {
      return this.$store.state.company.COMPANY;
    },
  },
  methods: {
    parseJsonDate: dates.parseJsonDate,
    buy: function() {
      if (this.confirm) {
        this.busy = true;
        this.actionedLeads = [];
        this.$api.leads.buyBulk(this.getLeadsArray()).then((response) => {
          this.busy = false;
          this.saleResult = response.data.data;
          this.markActioned();
          if (response.data.data.leads) {
            this.logGaTransaction(Object.values(response.data.data.leads));
          }
          this.expandRows();
          this.$store.dispatch("accountBalance");
          this.$emit('purchased');
        });
      }
    },
    getCreditLimit: function() {
      this.$api.company.remainingCredit().then(
        response => { this.creditLimit = response.data.data.remainingCredit }
      )
    },
    getLeadsArray: function() {
      return this.leads.map((lead) => lead[0].id);
    },
    isPurchased: function(lead) {
      if (this.saleResult === null) {
        return false;
      }
      return (this.saleResult.leads[lead[0].id] !== undefined);
    },
    isError: function(lead) {
      if (this.saleResult === null) {
        return false;
      }
      return (this.saleResult.error[lead[0].id] !== undefined);
    },
    getErrorMessage: function(lead) {
      if (this.saleResult === null) {
        return null;
      }
      if (this.saleResult.error[lead[0].id] !== undefined) {
        return this.$errorMessages.getErrorMessage('lead_availability', this.saleResult.error[lead[0].id][0]);
      }
      return null;
    },
    getLeadCode: function(lead) {
      if (this.saleResult === null) {
        return false;
      }
      return (this.saleResult.leads[lead[0].id] !== undefined) ? this.saleResult.leads[lead[0].id].code : null;
    },
    clearBasket: function() {
      this.getCreditLimit();
      this.$store.commit(CLEAR_SELECTED_LEADS);
      this.confirm = false;
      this.saleResult = null;
    },
    expandRows: function() {
      this.leads = this.leads.map(lead => ({...lead, _showDetails: true}));
    },
    markActioned: function() {
      this.leads.forEach((lead) => {
        if(this.isPurchased(lead)) {
          this.fireEvent(lead, "accepted");
          this.actionedLeads.push(lead[0].id);
        }
      });
    },
    logGaTransaction: function(leads) {
      if (leads.length < 1) {
        return;
      }
      this.logTransaction(leads, this.company, true);
    }
  },
  created: function(){
    this.getCreditLimit();
  }
}
</script>

<style scoped lang="scss">
.tableMessage {
  margin: -10px -5px 0;
  padding: 5px;
}

.remove-lead {
  display: none;
}

@media only screen and (min-width: 1060px) and (max-width: 1200px) {
  ::v-deep .table tbody {
    .itemised-leads {
      display: flex;
      width: 100%;
      flex-wrap: wrap;

      .align-middle {
        flex: 0 1 50%;
      }
    }
  }

  .remove-lead {
    display: block;
    float: left;
    line-height: 1;
    font-size: $font-size-14;
  }
}
</style>
